// client/src/components/notes/NoteEditorModal.jsx
//
// Create / edit a note and choose what it is associated with: any number of
// open events, event items, event actions, posts and campaigns — or nothing,
// in which case it stays pinned to the top of the Notes list.
//
// Opened from the Notes page and from the Notes panel on each detail page;
// the panel passes `initialLinks` so a new note starts linked to that record.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaTrash } from 'react-icons/fa';
import { createNote, updateNote, deleteNote, listNoteTargets } from '../../services/notesApi';
import { NOTE_REF_TYPES, typeLabel, typePlural, linkKey, flattenTargets } from './noteTypes';
import './Notes.css';

const MAX_RESULTS = 50;

export default function NoteEditorModal({
  tenantId,
  note = null,
  initialLinks = [],
  onClose,
  onSaved,
  onDeleted,
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(note?.id);
  const [title, setTitle] = useState(note?.title || '');
  const [body, setBody] = useState(note?.body || '');
  const [links, setLinks] = useState(() => (isEdit ? note.links || [] : initialLinks));
  const [targets, setTargets] = useState([]);
  const [targetsError, setTargetsError] = useState('');
  const [pickType, setPickType] = useState('all');
  const [pickQuery, setPickQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => { titleRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  useEffect(() => {
    let alive = true;
    listNoteTargets(tenantId)
      .then((res) => { if (alive) setTargets(flattenTargets(res.data)); })
      .catch((err) => { if (alive) setTargetsError(err.message || 'Could not load records'); });
    return () => { alive = false; };
  }, [tenantId]);

  const selected = useMemo(() => new Set(links.map(linkKey)), [links]);

  const results = useMemo(() => {
    const q = pickQuery.trim().toLowerCase();
    return targets
      .filter((tg) => !selected.has(linkKey(tg)))
      .filter((tg) => pickType === 'all' || tg.type === pickType)
      .filter((tg) => !q || `${tg.label} ${tg.sublabel || ''}`.toLowerCase().includes(q))
      .slice(0, MAX_RESULTS);
  }, [targets, selected, pickType, pickQuery]);

  const addLink = (tg) => setLinks((prev) => [...prev, tg]);
  const removeLink = (key) => setLinks((prev) => prev.filter((l) => linkKey(l) !== key));

  const canSave = (title.trim() || body.trim()) && !busy;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setError('');
    const payload = { title: title.trim(), body: body.trim(), links: links.map((l) => ({ type: l.type, id: l.id })) };
    try {
      const res = isEdit ? await updateNote(tenantId, note.id, payload) : await createNote(tenantId, payload);
      onSaved?.(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || t('notes.saveFailed', 'Could not save the note'));
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError('');
    try {
      await deleteNote(tenantId, note.id);
      onDeleted?.(note.id);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || t('notes.deleteFailed', 'Could not delete the note'));
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="notes-modal" role="presentation" onClick={() => !busy && onClose?.()}>
      <div
        className="surface notes-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="note-editor-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="notes-modal__head">
          <h3 id="note-editor-title">{isEdit ? t('notes.editNote', 'Edit note') : t('notes.newNote', 'New note')}</h3>
          <button type="button" className="notes-icon-btn" onClick={onClose} aria-label={t('common.close', 'Close')} disabled={busy}>
            <FaTimes />
          </button>
        </div>

        <label className="notes-field">
          <span>{t('notes.titleLabel', 'Title')}</span>
          <input
            ref={titleRef}
            type="text"
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('notes.titlePlaceholder', 'Optional')}
          />
        </label>

        <label className="notes-field">
          <span>{t('notes.bodyLabel', 'Note')}</span>
          <textarea
            value={body}
            rows={6}
            maxLength={20000}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('notes.bodyPlaceholder', 'Write your note…')}
          />
        </label>

        <div className="notes-field">
          <span>{t('notes.associatedWith', 'Associated with')}</span>
          {links.length === 0 ? (
            <div className="note info notes-modal__hint">
              {t('notes.unassociatedHint', 'Not associated with anything. Unassociated notes stay at the top of the Notes list.')}
            </div>
          ) : (
            <ul className="notes-chips" aria-label={t('notes.associatedWith', 'Associated with')}>
              {links.map((l) => (
                <li key={linkKey(l)} className="notes-chip">
                  <span className="notes-chip__type">{typeLabel(t, l.type)}</span>
                  <span className="notes-chip__label">{l.label}</span>
                  <button
                    type="button"
                    className="notes-chip__remove"
                    onClick={() => removeLink(linkKey(l))}
                    aria-label={`${t('notes.removeAssociation', 'Remove')} ${l.label}`}
                  >
                    <FaTimes />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="notes-picker">
            <div className="notes-picker__controls">
              <select
                value={pickType}
                onChange={(e) => setPickType(e.target.value)}
                aria-label={t('notes.pickType', 'Record type')}
              >
                <option value="all">{t('notes.allTypes', 'All types')}</option>
                {NOTE_REF_TYPES.map((type) => (
                  <option key={type} value={type}>{typePlural(t, type)}</option>
                ))}
              </select>
              <input
                type="search"
                value={pickQuery}
                onChange={(e) => setPickQuery(e.target.value)}
                placeholder={t('notes.pickSearch', 'Find an open event, item, action, post or campaign…')}
                aria-label={t('notes.pickSearchLabel', 'Find a record to associate')}
              />
            </div>
            {targetsError ? (
              <div className="note error notes-modal__hint">{targetsError}</div>
            ) : (
              <ul className="notes-picker__results" aria-label={t('notes.pickResults', 'Records you can associate')}>
                {results.length === 0 && (
                  <li className="surface-muted notes-picker__empty">{t('notes.pickNone', 'No matching open records.')}</li>
                )}
                {results.map((tg) => (
                  <li key={linkKey(tg)}>
                    <button type="button" className="notes-picker__result" onClick={() => addLink(tg)}>
                      <span className="notes-chip__type">{typeLabel(t, tg.type)}</span>
                      <span className="notes-picker__label">{tg.label}</span>
                      {tg.sublabel && <span className="surface-muted notes-picker__sub">{tg.sublabel}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {error && <div className="note error">{error}</div>}

        <div className="notes-modal__actions">
          {isEdit && !confirmDelete && (
            <button type="button" className="notes-btn notes-btn--danger" onClick={() => setConfirmDelete(true)} disabled={busy}>
              <FaTrash /> {t('common.delete', 'Delete')}
            </button>
          )}
          {isEdit && confirmDelete && (
            <span className="notes-modal__confirm">
              {t('notes.confirmDelete', 'Delete this note?')}
              <button type="button" className="notes-btn notes-btn--danger" onClick={remove} disabled={busy}>
                {t('notes.confirmDeleteYes', 'Yes, delete')}
              </button>
              <button type="button" className="notes-btn" onClick={() => setConfirmDelete(false)} disabled={busy}>
                {t('common.cancel', 'Cancel')}
              </button>
            </span>
          )}
          <span className="notes-modal__spacer" />
          <button type="button" className="notes-btn" onClick={onClose} disabled={busy}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button type="button" className="notes-btn notes-btn--primary" onClick={save} disabled={!canSave}>
            {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}
